import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getMatch, updateMatch, uploadMatchImage } from '../../api';
import CountrySelect from '../../components/CountrySelect';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';
import SaveButton from '../../components/SaveButton';
import Snackbar from '../../components/Snackbar';
import { getAllDivisions } from '../../utils/scoring';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function Settings() {
  const { matchId } = useParams();
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [copied, setCopied] = useState(false);
  const matchUrl = `${window.location.origin}/${match?.alias || matchId}`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getMatch(matchId);
        setMatch(data);
      } catch (err) {
        setSnack({ open: true, message: 'Failed to load match settings', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [matchId]);

  const handleSave = async (patch) => {
    setSaving(true);
    try {
      const { data } = await updateMatch(matchId, patch);
      setMatch((prev) => ({ ...prev, ...data, ...patch }));
      setSnack({ open: true, message: t('common.saveSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('common.saveError'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', field);
    try {
      setSaving(true);
      const { data } = await uploadMatchImage(matchId, formData);
      setMatch((prev) => ({ ...prev, [field]: data.url || data[field] }));
      setSnack({ open: true, message: 'Image uploaded', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: 'Image upload failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const isUS = (locale) => locale === 'en-US';
  const fmtDate = (d, locale, sep = '/') => {
    if (!d) return '';
    const mm = String(d.month).padStart(2, '0');
    const dd = String(d.day).padStart(2, '0');
    return isUS(locale) ? `${mm}${sep}${dd}${sep}${d.year}` : `${dd}${sep}${mm}${sep}${d.year}`;
  };
  const dateToMoment = (d) => d ? moment([d.year, d.month - 1, d.day]) : null;
  const momentToDate = (m) => m && m.isValid() ? { year: m.year(), month: m.month() + 1, day: m.date() } : null;
  const dateFmt = isUS(match?.locale) ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

  // Match date range patterns with / or - separators: XX/XX/XXXX or XX-XX-XXXX
  const dateRangeRegex = /(\d{2})([\/-])(\d{2})\2(\d{4})\s*-\s*(\d{2})\2(\d{2})\2(\d{4})/;

  const replaceDatesInDescription = (desc, oldStart, oldEnd, newStart, newEnd, oldLocale, newLocale) => {
    if (!desc || !oldStart || !oldEnd) return desc;
    const match = desc.match(dateRangeRegex);
    if (!match) return desc;
    // Preserve the separator used in the existing description
    const sep = match[2];
    const newRange = `${fmtDate(newStart || oldStart, newLocale, sep)} - ${fmtDate(newEnd || oldEnd, newLocale, sep)}`;
    return desc.replace(dateRangeRegex, newRange);
  };

  const updateField = (field, value) => {
    setMatch((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'locale') {
        // Reformat dates in description when locale changes
        updated.description = replaceDatesInDescription(
          prev.description, prev.startDate, prev.endDate,
          prev.startDate, prev.endDate, prev.locale, value,
        );
      }
      if (field === 'startDate' || field === 'endDate') {
        const locale = prev.locale || 'en-US';
        const newStart = field === 'startDate' ? value : prev.startDate;
        const newEnd = field === 'endDate' ? value : prev.endDate;
        updated.description = replaceDatesInDescription(
          prev.description, prev.startDate, prev.endDate,
          newStart, newEnd, locale, locale,
        );
      }
      return updated;
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (!match) return null;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" gutterBottom>{t('matches.settings')}</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
        <Tab label={t('manage.tabGeneral', 'General')} />
        <Tab label={t('manage.tabSitePages', 'Site Pages')} />
        <Tab label={t('manage.tabRegistration', 'Registration')} />
        <Tab label={t('manage.tabScoring', 'Scoring')} />
        <Tab label={t('manage.tabNotifications', 'Notifications')} />
        <Tab label={t('manage.tabContacts', 'Contacts')} />
      </Tabs>

      {/* Site Pages Tab */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Visibility */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5 }}>
            <Typography variant="h6">{t('manage.sitePages', 'Site Pages')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('manage.sitePagesDesc', 'Configure which pages in your site are available to shooters')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={<Switch checked={!!match.stagesPublished} onChange={(e) => updateField('stagesPublished', e.target.checked)} />}
                label={t('manage.stagesPublished', 'Stages are ready to be seen by shooters')}
              />
              <FormControlLabel
                control={<Switch checked={!!match.squadsPublished} onChange={(e) => updateField('squadsPublished', e.target.checked)} />}
                label={t('manage.squadsPublished', 'Squads and shooters list can be viewed by shooters')}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Switch checked={!!match.scoresPublished} onChange={(e) => updateField('scoresPublished', e.target.checked ? (match.scoresPublished || 'full') : null)} />}
                  label={t('manage.scoresPublished', 'Score sheet (partial or full) are available for shooters')}
                />
                {!!match.scoresPublished && (
                  <FormControlLabel
                    control={<Checkbox checked={match.scoresPublished === 'verify-only'} onChange={(e) => updateField('scoresPublished', e.target.checked ? 'verify-only' : 'full')} />}
                    label={t('manage.scoresVerifyOnly', 'Show only verify')}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Shooter Names */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5 }}>
            <Typography variant="h6">{t('manage.shooterNames', 'Shooter Names')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('manage.shooterNamesDesc', 'How are shooter names displayed on the public website?')}
            </Typography>
            <RadioGroup row value={match.nameAnonymizationScheme || 'none'} onChange={(e) => updateField('nameAnonymizationScheme', e.target.value)}>
              <FormControlLabel value="none" control={<Radio />} label={t('manage.namesFull', 'Full name (eg. "John Doe")')} />
              <FormControlLabel value="firstname" control={<Radio />} label={t('manage.namesFirstAbbrev', 'First name abbreviated (eg. "J. Doe")')} />
              <FormControlLabel value="lastname" control={<Radio />} label={t('manage.namesLastAbbrev', 'Last name abbreviated (eg. "John D.")')} />
            </RadioGroup>
          </Box>

          <SaveButton onClick={() => handleSave({
            stagesPublished: match.stagesPublished,
            squadsPublished: match.squadsPublished,
            scoresPublished: match.scoresPublished,
            nameAnonymizationScheme: match.nameAnonymizationScheme,
          })} saving={saving} />
        </Box>
      </TabPanel>

      {/* General Tab */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label={t('manage.matchTitle', 'Match Title')}
            value={match.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            fullWidth
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -1.5 }}>
            <Typography variant="body2" component="a" href={matchUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', wordBreak: 'break-all' }}>
              {matchUrl}
            </Typography>
            <Tooltip title={t('manage.copied', 'Copied!')} disableHoverListener disableFocusListener open={copied} onClose={() => setCopied(false)}>
              <IconButton size="small" onClick={() => { navigator.clipboard.writeText(matchUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', backgroundColor: 'action.hover' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {match.type === 'ipsc-shotgun' ? 'IPSC Shotgun' : match.type === 'ipsc-pcc' ? 'IPSC PCC' : 'IPSC Handgun'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('manage.matchTypeHint', 'Contact us to change (we support IPSC Handgun, IPSC PCC and IPSC Shotgun)')}
            </Typography>
          </Box>
          <FormControl fullWidth>
            <InputLabel>{t('manage.ipscLevel', 'IPSC Level')}</InputLabel>
            <Select
              value={typeof match.ipscLevel === 'undefined' ? 1 : match.ipscLevel}
              label={t('manage.ipscLevel', 'IPSC Level')}
              onChange={(e) => updateField('ipscLevel', e.target.value)}
            >
              <MenuItem value={0}>{t('manage.ipscLevel0', 'Club Practice / Unapproved Match')}</MenuItem>
              <MenuItem value={1}>{t('manage.ipscLevel1', 'Internal Club Match (Approved Level 1)')}</MenuItem>
              <MenuItem value={2}>{t('manage.ipscLevel2', 'Cross-Club Match (Approved Level 2)')}</MenuItem>
              <MenuItem value={3}>{t('manage.ipscLevel3', 'Regional Championship (Approved Level 3)')}</MenuItem>
              <MenuItem value={4}>{t('manage.ipscLevel4', 'Continental Championships (Approved Level 4)')}</MenuItem>
              <MenuItem value={5}>{t('manage.ipscLevel5', 'The World Shoots (Approved Level 5)')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('manage.description', 'Description')}
            value={match.description || ''}
            onChange={(e) => updateField('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          <Box>
            <Typography variant="subtitle2">{t('manage.about', 'About')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('manage.aboutHint', "Tell your visitors what's special about your match, and we will add it to the main page")}
            </Typography>
            <ReactQuill
              theme="snow"
              value={match.about || ''}
              onChange={(val) => updateField('about', val)}
              style={{ height: 240 }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ color: [] }, { background: [] }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  [{ align: [] }],
                  ['link', 'image'],
                  ['clean'],
                ],
              }}
            />
            <Box sx={{ mb: 5 }} />
          </Box>
          <CountrySelect
            label={t('manage.region', 'Region')}
            value={match.countryCode || ''}
            onChange={(code) => updateField('countryCode', code)}
            sx={{ width: '100%' }}
          />
          <FormControlLabel
            control={<Switch checked={!!match.showInSearch} onChange={(e) => updateField('showInSearch', e.target.checked)} />}
            label={t('manage.showInRegional', 'Show in regional match listings')}
          />
          <FormControl fullWidth>
            <InputLabel>{t('manage.locale', 'Locale')}</InputLabel>
            <Select value={match.locale || 'en-US'} label={t('manage.locale', 'Locale')} onChange={(e) => updateField('locale', e.target.value)}>
              <MenuItem value="en-US">English (US)</MenuItem>
              <MenuItem value="he-IL">Hebrew (IL)</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label={t('manage.startDate', 'Start Date')}
                format={dateFmt}
                value={dateToMoment(match.startDate)}
                onChange={(m) => { const d = momentToDate(m); if (d) updateField('startDate', d); }}
                sx={{ flex: 1 }}
              />
              <DatePicker
                label={t('manage.endDate', 'End Date')}
                format={dateFmt}
                value={dateToMoment(match.endDate)}
                onChange={(m) => { const d = momentToDate(m); if (d) updateField('endDate', d); }}
                sx={{ flex: 1 }}
              />
            </Box>
          </LocalizationProvider>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            {t('manage.lockHint', 'Match will be locked 48 hours after this date.')}
          </Typography>
          <PlacesAutocomplete
            label={t('manage.location', 'Location')}
            value={match.location}
            locale={match.locale}
            onChange={(loc) => updateField('location', loc)}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('manage.logo', 'Logo')}</Typography>
              {match.logo && <Box component="img" src={match.logo} sx={{ maxWidth: 120, maxHeight: 80, mb: 1, display: 'block' }} />}
              <Button variant="outlined" component="label" size="small">
                {t('manage.uploadLogo', 'Upload Logo')}
                <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload('logo', e.target.files[0])} />
              </Button>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('manage.coverImage', 'Cover Image')}</Typography>
              {match.cover && <Box component="img" src={match.cover} sx={{ maxWidth: 200, maxHeight: 80, mb: 1, display: 'block' }} />}
              <Button variant="outlined" component="label" size="small">
                {t('manage.uploadCover', 'Upload Cover')}
                <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload('cover', e.target.files[0])} />
              </Button>
            </Box>
          </Box>
          <SaveButton onClick={() => {
            const { countryCode: _lcc, ...loc } = match.location || {};
            handleSave({
              title: match.title, description: match.description, about: match.about,
              locale: match.locale, countryCode: match.countryCode, ipscLevel: match.ipscLevel,
              showInSearch: match.showInSearch, startDate: match.startDate, endDate: match.endDate,
              location: loc.formatted ? loc : undefined,
            });
          }} saving={saving} />
        </Box>
      </TabPanel>

      {/* Registration Tab */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Registration Status */}
          <Box>
            <Typography variant="h6" gutterBottom>{t('manage.regTitle', 'Registration')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('manage.regSubtitle', 'Configure how shooters register to your match')}</Typography>
            <FormControl component="fieldset">
              <RadioGroup value={match.registration || 'closed'} onChange={(e) => updateField('registration', e.target.value)}>
                <FormControlLabel value="closed" control={<Radio />} label={t('manage.regClosed', 'Closed (registration not on web site)')} />
                <FormControlLabel value="open" control={<Radio />} label={t('manage.regOpen', 'Open on web site')} />
                <FormControlLabel
                  value="open-code"
                  sx={{ alignItems: 'flex-start', py: 1 }}
                  control={<Radio sx={{ pt: 0 }} />}
                  label={
                    <Box>
                      <Typography>{t('manage.regOpenCode', 'On web site, but requires special code to access')}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <TextField
                          size="small"
                          disabled={match.registration !== 'open-code'}
                          value={match.registrationCode?.value || ''}
                          onChange={(e) => updateField('registrationCode', e.target.value ? { type: 'plain', value: e.target.value } : null)}
                          placeholder={t('manage.regCodePlaceholder', 'Access code')}
                          error={match.registration === 'open-code' && !match.registrationCode?.value}
                          sx={{ direction: 'ltr' }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={match.registration !== 'open-code'}
                          onClick={() => updateField('registrationCode', { type: 'plain', value: String(10000 + Math.floor(Math.random() * 90000)) })}
                        >
                          {t('manage.regRandomCode', 'Random')}
                        </Button>
                      </Box>
                    </Box>
                  }
                />
                <FormControlLabel value="sold-out" control={<Radio />} label={t('manage.regSoldOut', "Closed (we're fully booked)")} />
                <FormControlLabel value="period-over" control={<Radio />} label={t('manage.regPeriodOver', 'Closed (registration period is over)')} />
                <FormControlLabel value="period-over-can-patch" control={<Radio />} label={t('manage.regPeriodOverPatch', 'Closed (registration period is over) - However, shooters can still update information.')} />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Registration Fields */}
          {(match.registration === 'open' || match.registration === 'open-code') && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('manage.regFields', 'What information do shooters need to enter during registration?')}</Typography>
              {(() => {
                const fields = match.registrationExtraFields || [];
                const getState = (key) => {
                  if (fields.includes(key)) return 'required';
                  if (fields.includes(`${key}_optional`)) return 'optional';
                  return 'off';
                };
                const setState = (key, newState) => {
                  let updated = fields.filter((f) => f !== key && f !== `${key}_optional`);
                  if (newState === 'required') updated.push(key);
                  else if (newState === 'optional') updated.push(`${key}_optional`);
                  updateField('registrationExtraFields', updated);
                };
                const extraFields = [
                  { key: 'shooter_address', label: t('shooter.address') },
                  { key: 'shooter_phone', label: t('shooter.phone') },
                  { key: 'shooter_social_number', label: t('shooter.socialNumber') },
                  { key: 'shooter_gun_license', label: t('shooter.gunLicense') },
                  { key: 'shooter_gun_license_exp', label: t('shooter.gunLicenseExp') },
                  { key: 'gun_make_and_model', label: t('shooter.gunMakeAndModel') },
                  { key: 'gun_serial', label: t('shooter.gunSerial') },
                  { key: 'gun_serial_2', label: t('shooter.gunSerial2', 'Second Gun Serial Number') },
                  { key: 'covid', label: t('manage.regCovid', 'Covid-19 (vacinated/immuned/tested)') },
                  { key: 'image', label: t('shooter.image') },
                ];
                const alwaysRequired = [
                  { label: t('shooter.id') },
                  { label: t('shooter.name') },
                  { label: t('shooter.club') },
                  { label: t('shooter.email') },
                  { label: t('divisions.division', 'Division') },
                  { label: t('divisions.category', 'Category') },
                  { label: t('shooter.powerFactor', 'Power Factor') },
                ];
                return (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', px: 2, py: 1 }}>
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{t('manage.regFieldName', 'Field')}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, minWidth: 240, justifyContent: 'center' }}>
                        <Typography variant="caption" sx={{ width: 70, textAlign: 'center', fontWeight: 600 }}>{t('manage.regFieldOff', 'Off')}</Typography>
                        <Typography variant="caption" sx={{ width: 70, textAlign: 'center', fontWeight: 600 }}>{t('common.optional', 'Optional')}</Typography>
                        <Typography variant="caption" sx={{ width: 70, textAlign: 'center', fontWeight: 600 }}>{t('manage.regFieldRequired', 'Required')}</Typography>
                      </Box>
                    </Box>
                    {/* Always-required rows */}
                    {alwaysRequired.map(({ label }) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider', opacity: 0.6 }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, minWidth: 240, justifyContent: 'center' }}>
                          <Box sx={{ width: 70, textAlign: 'center' }}><Radio size="small" disabled /></Box>
                          <Box sx={{ width: 70, textAlign: 'center' }}><Radio size="small" disabled /></Box>
                          <Box sx={{ width: 70, textAlign: 'center' }}><Radio size="small" checked disabled /></Box>
                        </Box>
                      </Box>
                    ))}
                    {/* Toggleable rows */}
                    {extraFields.map(({ key, label }) => {
                      const state = getState(key);
                      return (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
                          <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
                          <Box sx={{ display: 'flex', gap: 1, minWidth: 240, justifyContent: 'center' }}>
                            <Box sx={{ width: 70, textAlign: 'center' }}>
                              <Radio size="small" checked={state === 'off'} onChange={() => setState(key, 'off')} />
                            </Box>
                            <Box sx={{ width: 70, textAlign: 'center' }}>
                              <Radio size="small" checked={state === 'optional'} onChange={() => setState(key, 'optional')} />
                            </Box>
                            <Box sx={{ width: 70, textAlign: 'center' }}>
                              <Radio size="small" checked={state === 'required'} onChange={() => setState(key, 'required')} />
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                    {/* Equipment belt row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>{t('equipment', 'Equipment Belt')}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, minWidth: 240, justifyContent: 'center' }}>
                        <Box sx={{ width: 70, textAlign: 'center' }}>
                          <Radio size="small" checked={!fields.includes('equipment')} onChange={() => {
                            updateField('registrationExtraFields', fields.filter((f) => f !== 'equipment'));
                          }} />
                        </Box>
                        <Box sx={{ width: 70, textAlign: 'center' }}><Radio size="small" disabled /></Box>
                        <Box sx={{ width: 70, textAlign: 'center' }}>
                          <Radio size="small" checked={fields.includes('equipment')} onChange={() => {
                            updateField('registrationExtraFields', [...fields.filter((f) => f !== 'equipment'), 'equipment']);
                          }} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}

          {/* Equipment belt updates in "closed but can update" mode. The full
              registration-fields table above is only shown while registration is
              open, so expose the belt toggle here so managers can let shooters
              update their belt after registration closes. */}
          {match.registration === 'period-over-can-patch' && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('manage.regFieldsPatch', 'What can shooters update?')}</Typography>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={(match.registrationExtraFields || []).includes('equipment')}
                    onChange={(e) => {
                      const fields = match.registrationExtraFields || [];
                      updateField('registrationExtraFields', e.target.checked
                        ? [...fields.filter((f) => f !== 'equipment'), 'equipment']
                        : fields.filter((f) => f !== 'equipment'));
                    }}
                  />
                )}
                label={t('equipment', 'Equipment Belt')}
              />
            </Box>
          )}

          {/* Locked fields */}
          {(match.registration === 'open' || match.registration === 'open-code' || match.registration === 'period-over-can-patch') && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('manage.regLockedFields', 'What information shooters cannot change after registration?')}</Typography>
              <FormControlLabel
                control={<Checkbox checked={(match.registrationPatchLockedFields || []).includes('publicId')} onChange={(e) => {
                  updateField('registrationPatchLockedFields', e.target.checked ? ['publicId'] : []);
                }} />}
                label={t('shooter.id')}
              />
            </Box>
          )}

          {/* ICS Classification */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('manage.regIcs', 'ICS Classification')}</Typography>
            <FormControlLabel
              control={<Checkbox checked={!!match.icsEnabled} onChange={(e) => updateField('icsEnabled', e.target.checked)} />}
              label={t('manage.regIcsCheckbox', 'Shooter registrations and scores should include ICS classification.')}
            />
          </Box>

          {/* Divisions */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('manage.regDivisions', 'Match includes divisions:')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
              {getAllDivisions({ matchType: match.type }).map((div) => {
                const divKey = div.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                return (
                  <FormControlLabel
                    key={div}
                    control={<Checkbox checked={!(match.registrationExcludeDivisions || []).includes(div)} onChange={() => {
                      const excluded = [...(match.registrationExcludeDivisions || [])];
                      updateField('registrationExcludeDivisions', excluded.includes(div) ? excluded.filter((d) => d !== div) : [...excluded, div]);
                    }} />}
                    label={t(`divisions.${divKey}`, div)}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Registration Limit */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('manage.regLimit', 'Limit registration')}</Typography>
            <FormControlLabel
              control={<Checkbox checked={!!match.registrationLimitGSheet} onChange={(e) => {
                updateField('registrationLimitGSheet', e.target.checked ? (match.registrationLimitGSheet || ' ') : '');
              }} />}
              label={t('manage.regLimitCheckbox', 'Limit registration only to authorized shooters listed in the following Google Sheets document:')}
            />
            <TextField
              fullWidth
              disabled={!match.registrationLimitGSheet}
              value={match.registrationLimitGSheet?.trim() || ''}
              onChange={(e) => updateField('registrationLimitGSheet', e.target.value || ' ')}
              placeholder="https://docs.google.com/spreadsheets/..."
              sx={{ direction: 'ltr', mt: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('manage.regLimitDesc', 'The Google Sheet document should include 5 columns - Shooter Region, Shooter Id, Shooter Full Name, and Shooter Club. Also, leave first row blank, or use it for titles. Get a view-only sharable link to the sheet (using the Share button in Google Sheets), and paste the URL to the field above.')}
            </Typography>
          </Box>

          <Divider />

          {/* Squad Selection */}
          <Box>
            <Typography variant="h6" gutterBottom>{t('manage.squadSelection', 'Squad Selection')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('manage.squadSelectionDesc', 'Configure when shooters pick their squads')}
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup value={match.squadSelection || 'closed'} onChange={(e) => updateField('squadSelection', e.target.value)}>
                <FormControlLabel value="closed" control={<Radio />} label={t('manage.squadClosed', "Shooters don't pick their squads. Squad selection is made by staff.")} />
                <FormControlLabel value="during-registration" control={<Radio />} label={t('manage.squadDuringReg', 'Shooters may pick their squads during registration')} />
                <FormControlLabel value="after-payment" control={<Radio />} label={t('manage.squadAfterPayment', 'Shooters may pick their squads only after they have paid for the match')} />
              </RadioGroup>
            </FormControl>
          </Box>

          <Divider />

          {/* RO Registration */}
          <Box>
            <Typography variant="h6" gutterBottom>{t('manage.roRegTitle', 'RO Registration')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('manage.roRegSubtitle', 'Configure how ROs register for your match')}</Typography>
            <FormControl component="fieldset">
              <RadioGroup value={match.roRegistration || 'closed'} onChange={(e) => updateField('roRegistration', e.target.value)}>
                <FormControlLabel value="closed" control={<Radio />} label={t('manage.regClosed', 'Closed (registration not on web site)')} />
                <FormControlLabel value="open" control={<Radio />} label={t('manage.regOpen', 'Open on web site')} />
                <FormControlLabel
                  value="open-code"
                  sx={{ alignItems: 'flex-start', py: 1 }}
                  control={<Radio sx={{ pt: 0 }} />}
                  label={
                    <Box>
                      <Typography>{t('manage.regOpenCode', 'On web site, but requires special code to access')}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <TextField
                          size="small"
                          disabled={match.roRegistration !== 'open-code'}
                          value={match.roRegistrationCode?.value || ''}
                          onChange={(e) => updateField('roRegistrationCode', e.target.value ? { type: 'plain', value: e.target.value } : null)}
                          placeholder={t('manage.regCodePlaceholder', 'Access code')}
                          error={match.roRegistration === 'open-code' && !match.roRegistrationCode?.value}
                          sx={{ direction: 'ltr' }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={match.roRegistration !== 'open-code'}
                          onClick={() => updateField('roRegistrationCode', { type: 'plain', value: String(10000 + Math.floor(Math.random() * 90000)) })}
                        >
                          {t('manage.regRandomCode', 'Random')}
                        </Button>
                      </Box>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <SaveButton
            disabled={(match.registration === 'open-code' && !match.registrationCode?.value)
              || (match.roRegistration === 'open-code' && !match.roRegistrationCode?.value)}
            onClick={() => handleSave({
              registration: match.registration,
              registrationCode: match.registrationCode,
              registrationExtraFields: match.registrationExtraFields,
              registrationPatchLockedFields: match.registrationPatchLockedFields,
              registrationExcludeDivisions: match.registrationExcludeDivisions,
              registrationLimitGSheet: match.registrationLimitGSheet || null,
              icsEnabled: match.icsEnabled,
              squadSelection: match.squadSelection,
              roRegistration: match.roRegistration,
              roRegistrationCode: match.roRegistrationCode,
            })} saving={saving} />
        </Box>
      </TabPanel>

      {/* Scoring Tab */}
      <TabPanel value={tab} index={3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Scoring Summary Page */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5 }}>
            <Typography variant="h6">{t('manage.scoringSummary', 'Scoring Summary Page')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('manage.scoringSummaryDesc', "Select what information would you like the scoring summary page, on the RO's app, to contain: (not, this does not affect the email sent, which will show full information)")}
            </Typography>
            <RadioGroup value={match.scoreFormDisplayMode || 'normal'} onChange={(e) => updateField('scoreFormDisplayMode', e.target.value)}>
              <FormControlLabel value="normal" control={<Radio />} label={t('manage.scoringFull', 'Full information - including breakdown of scores per targets.')} />
              <FormControlLabel value="corona" control={<Radio />} label={t('manage.scoringCondensed', 'Condensed summary - including only aggregated count of shots per score & penalties.')} />
            </RadioGroup>
          </Box>

          {/* Scoring Method */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5 }}>
            <Typography variant="h6">{t('manage.scoringMethod', 'Scoring Method')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('manage.scoringMethodDesc', 'Select how you want to competition to be scored')}
            </Typography>
            <RadioGroup value={match.scoreMode || 'comstock'} onChange={(e) => updateField('scoreMode', e.target.value)}>
              <FormControlLabel value="comstock" control={<Radio />} label={t('manage.scoreComstock', 'Standard comstock scoring, as defined in 9.2.1 of the rule book.')} />
              <FormControlLabel value="steel-no-miss" control={<Radio />} label={t('manage.scoreSteel', 'Steel challenge scoring. No points deducted for a miss.')} />
              <FormControlLabel value="comstock-abd" control={<Radio />} label={t('manage.scoreComstockAbd', 'Standard comstock scoring, with the exception that charlie scores are marked as B instead of C.')} />
              <FormControlLabel value="il-army" control={<Radio />} label={t('manage.scoreBattle', 'Battle challenge scoring. No (D) and battle criteria bonuses.')} />
            </RadioGroup>
          </Box>

          <SaveButton onClick={() => handleSave({ scoreMode: match.scoreMode, scoreFormDisplayMode: match.scoreFormDisplayMode })} saving={saving} />
        </Box>
      </TabPanel>

      {/* Notifications Tab */}
      <TabPanel value={tab} index={4}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5 }}>
            <Typography variant="h6">{t('manage.emails', 'Emails')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('manage.emailsDesc', 'Configure who should receive automatic emails and when.')}
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('manage.emailsScores', 'Scores')}</Typography>
            {(() => {
              const scores = match.notifications?.scores || [];
              const shooterNotif = scores.find((n) => n.destination === 'shooter' && n.channel === 'email');
              const staffNotif = scores.find((n) => n.destination === 'staff' && n.channel === 'email');
              const toggleNotif = (dest, active) => {
                let newScores = [...scores];
                const idx = newScores.findIndex((n) => n.destination === dest && n.channel === 'email');
                if (idx >= 0) {
                  newScores[idx] = { ...newScores[idx], active };
                } else {
                  newScores.push({ destination: dest, channel: 'email', active });
                }
                updateField('notifications', { ...match.notifications, scores: newScores });
              };
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={!!shooterNotif?.active} onChange={(e) => toggleNotif('shooter', e.target.checked)} />}
                    label={t('manage.emailShooters', 'Email scores to shooters when they finish a stage.')}
                  />
                  <FormControlLabel
                    control={<Switch checked={!!staffNotif?.active} onChange={(e) => toggleNotif('staff', e.target.checked)} />}
                    label={t('manage.emailStaff', 'Email scores to staff when shooters finish a stage.')}
                  />
                </Box>
              );
            })()}
          </Box>
          <SaveButton onClick={() => handleSave({ notifications: match.notifications })} saving={saving} />
        </Box>
      </TabPanel>

      {/* Contacts Tab */}
      <TabPanel value={tab} index={5}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5 }}>
            <Typography variant="h6">{t('manage.contacts', 'Contacts')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('manage.contactsDesc', 'Contact numbers for staff')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ minWidth: 400 }}>
                  {t('manage.contactRm', 'To contact the RM, staff should call or send a message this number:')}
                </Typography>
                <TextField
                  size="small"
                  value={match.contacts?.rm || ''}
                  onChange={(e) => updateField('contacts', { ...match.contacts, rm: e.target.value })}
                  sx={{ width: 200, direction: 'ltr' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ minWidth: 400 }}>
                  {t('manage.contactTech', 'To contact on-site tech support, staff should call or send a message this number:')}
                </Typography>
                <TextField
                  size="small"
                  value={match.contacts?.tech || ''}
                  onChange={(e) => updateField('contacts', { ...match.contacts, tech: e.target.value })}
                  sx={{ width: 200, direction: 'ltr' }}
                />
              </Box>
            </Box>
          </Box>
          <SaveButton onClick={() => handleSave({ contacts: match.contacts })} saving={saving} />
        </Box>
      </TabPanel>

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} />
    </Container>
  );
}
